import React from "react";
import {
  Box,
  IconButton,
  Typography,
  Divider,
  Avatar,
  Tooltip,
  Chip,
  LinearProgress,
  TextField,
  useTheme,
} from "@mui/material";
import {
  ArrowBack,
  Menu as MenuIcon,
  OpenInNew as OpenInNewIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import MessageHistory from "../../../components/MessageHistory";
import Placeholder from "../../../components/Placeholder";

import { getChannelIcon, getClientProfilePictureUrl, getInitials } from "../../../utils/utils";
import { useRequests } from "../../../hooks/useRequests";
import { useUsers } from "../../../hooks/useUsers";
import { useAgents } from "../../../hooks/useAgents";
import { useClients } from "../../../hooks/useClients";
import StatusChipSelector, {
  normalizeStatusInput,
} from "../../../components/StatusChipSelector";
import AssigneeSelector from "../../../components/AssigneeSelector";
import { getMessageHistoryWidth } from "../utils/layoutUtils";
import { getWhatsAppWindowState, getLastClientMessageDate } from "../../../utils/whatsappWindowUtils";
import { useTranslation } from "react-i18next";
import { useChannels } from "../../../hooks/useChannels";
import { isWhatsAppQrChannelTipo } from "../../../constants/channelCatalog";

/* ================= MessageHistoryPane ================= */

const MessageHistoryPane = ({
  isMobile,
  containerStyle,
  selectedConversation,
  showConversationList,
  showConversationDetails,
  userData,
  isLoadingDetails,
  initialLoading,
  isLoadingConversation,
  messages,
  onSendMessage,
  onEndConversation,
  onOpenPopup,
  onBack,
  onShowDetails,
  onUpdateConversation,
  onNewConversation,
  hasMoreMessages,
  isLoadingMoreMessages,
  onLoadMoreMessages,
  onRefetchConversationDetails,
  onDeleteMessage,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("chat");
  const { updateMutation } = useRequests();
  // Precisa incluir ChatBots (ura=1) no seletor de responsável (transferir/atribuir).
  const { data: usersResp } = useUsers(1, 150, "", "todos");
  const { data: agentsResp } = useAgents(1, 200, "");
  const { updateMutation: updateClientMutation } = useClients(
    1,
    10,
    "",
    null,
    { enabled: false }
  );

  const users = usersResp?.users ?? [];
  const agents = agentsResp?.agents ?? [];
  const { data: channelsData } = useChannels(1, 200, "");

  const getWidth = () => {
    if (isMobile) return "100%";
    return getMessageHistoryWidth({
      selectedConversation,
      showConversationList,
      showConversationDetails,
    });
  };

  const displayName =
    selectedConversation?.name || selectedConversation?.nome || "";

  const displayProtocol =
    selectedConversation?.protocol || selectedConversation?.id || "";

  const [isEditingName, setIsEditingName] = React.useState(false);
  const [nameDraft, setNameDraft] = React.useState("");

  React.useEffect(() => {
    if (!selectedConversation) return;
    setIsEditingName(false);
    setNameDraft(selectedConversation?.nome || selectedConversation?.name || "");
  }, [selectedConversation?.id]);

  const canEditName = Boolean(selectedConversation?.clientId);

  const commitName = (rawValue) => {
    if (!selectedConversation?.clientId) return;
    const next = String(rawValue ?? "").trim();
    if (!next) return;

    const current = String(selectedConversation?.nome || selectedConversation?.name || "").trim();
    if (next === current) {
      setIsEditingName(false);
      return;
    }

    updateClientMutation.mutate(
      { id: selectedConversation.clientId, nome: next },
      {
        onSuccess: () => {
          onUpdateConversation?.({ nome: next, name: next });
          setIsEditingName(false);
        },
        onError: (err) => {
          console.error(t("pane.errClientName"), err);
        },
      }
    );
  };

  const cancelEditName = () => {
    setIsEditingName(false);
    setNameDraft(selectedConversation?.nome || selectedConversation?.name || "");
  };

  const parseTags = (str) => {
    if (!str || typeof str !== "string") return [];
    return str.split(",").map((t) => t.trim()).filter(Boolean);
  };
  const clientTags = parseTags(
    selectedConversation?.clientTags ?? selectedConversation?.client?.tags ?? ""
  );
  const requestTags = parseTags(selectedConversation?.tags ?? "");
  const hasTags = clientTags.length > 0 || requestTags.length > 0;

  const displayChannel =
    selectedConversation?.channelLabel || selectedConversation?.channel || "";

  const channelType =
    selectedConversation?.channelType ??
    selectedConversation?.channel_type ??
    (() => {
      const ch = (selectedConversation?.channel ?? selectedConversation?.channelLabel ?? "").toString().toLowerCase();
      if (ch.includes("whatsapp")) return "WhatsApp";
      return null;
    })();
  const lastClientDate = getLastClientMessageDate(messages ?? []);
  const channelIdForWindow =
    selectedConversation?.channelId ??
    selectedConversation?.id_origem ??
    selectedConversation?.channel_id ??
    null;
  const channelTipoForWindow = React.useMemo(() => {
    const channelList = Array.isArray(channelsData?.channels) ? channelsData.channels : [];
    if (channelIdForWindow == null) return null;
    const ch = channelList.find((c) => String(c.id) === String(channelIdForWindow));
    return ch?.tipo != null && ch?.tipo !== "" ? Number(ch.tipo) : null;
  }, [channelsData, channelIdForWindow]);
  const whatsAppWindowState =
    channelType &&
    String(channelType).toLowerCase() === "whatsapp" &&
    !isWhatsAppQrChannelTipo(channelTipoForWindow)
      ? getWhatsAppWindowState(lastClientDate)
      : null;
  const showWhatsAppWindowIcon =
    whatsAppWindowState &&
    (whatsAppWindowState.status === "aboutToExpire" || whatsAppWindowState.status === "expired");

  const assignedToValue =
    selectedConversation?.assignedTo ?? selectedConversation?.userName ?? null;

  const assignedIsQueueLabel =
    typeof assignedToValue === "string" &&
    ["fila", "queue"].includes(assignedToValue.toLowerCase());

  const hasUserAssigned =
    assignedToValue !== null &&
    assignedToValue !== undefined &&
    assignedToValue !== 0 &&
    assignedToValue !== "0" &&
    assignedToValue !== "" &&
    !assignedIsQueueLabel;

  const agentName =
    selectedConversation?.agentName ?? selectedConversation?.agent_name ?? null;
  const rawAgentId =
    selectedConversation?.agentId ?? selectedConversation?.id_agente ?? null;
  const hasAgentAssigned =
    rawAgentId !== null &&
    rawAgentId !== "" &&
    rawAgentId !== 0 &&
    rawAgentId !== "0";

  const currentAssignee = React.useMemo(() => {
    if (!selectedConversation) {
      return { type: "queue", id: null, label: t("pane.queue") };
    }

    const status = normalizeStatusInput(selectedConversation?.status);
    const isClosed = status === 2 || status === 4 || status === 5;

    if (hasAgentAssigned) {
      const id = rawAgentId;
      const label = agentName || t("pane.aiAgent");
      return { type: "agent", id, label };
    }

    if (hasUserAssigned || selectedConversation?.userId) {
      const id = selectedConversation?.userId ?? null;
      const fromList =
        id != null ? users.find((u) => String(u.id) === String(id)) || null : null;
      const isChatBot =
        fromList
          ? Number(fromList.ura) === 1
          : Number(selectedConversation?.userUra) === 1;
      const baseLabel =
        assignedToValue || selectedConversation?.userName || "";

      const isCurrentUser =
        (id != null && String(id) === String(userData?.id)) ||
        (baseLabel && baseLabel === userData?.nome);

      const label = isCurrentUser
        ? t("pane.myRequest")
        : baseLabel || (isChatBot ? t("pane.chatBot") : t("pane.user"));

      return { type: "user", id, label, isChatBot };
    }

    return {
      type: "queue",
      id: null,
      label: isClosed ? t("pane.unassigned") : t("pane.queue"),
    };
  }, [
    hasAgentAssigned,
    hasUserAssigned,
    agentName,
    assignedToValue,
    t,
    selectedConversation,
    userData,
    users,
  ]);

  const handleStatusChange = (newStatus) => {
    if (!selectedConversation?.id) return;

    updateMutation.mutate(
      { id: selectedConversation.id, status: newStatus },
      {
        onSuccess: () => {
          onUpdateConversation?.({ status: newStatus });
          // Garante que o histórico e demais detalhes da solicitação
          // sejam recarregados imediatamente após a mudança de status.
          if (typeof onRefetchConversationDetails === "function") {
            onRefetchConversationDetails();
          }
        },
        onError: (err) => {
          console.error(t("pane.errStatus"), err);
        },
      }
    );
  };

  const handleAssigneeChange = (next) => {
    if (!selectedConversation?.id || !next) return;

    const payload = { id: selectedConversation.id };
    const updates = {};

    if (next.type === "queue") {
      payload.id_usuario = null;
      payload.id_agente = null;
      updates.userId = null;
      updates.assignedTo = null;
      updates.userName = null;
      updates.agentId = null;
      updates.agentName = null;
    } else if (next.type === "agent") {
      // Resolve o ID do agente primeiro a partir do objeto bruto,
      // depois cai para o next.id e, por último, para o nome.
      let agentId = null;

      if (next.raw) {
        const a = next.raw;
        agentId =
          a.id ??
          a.id_agente ??
          a.agentId ??
          null;
      }

      if (!agentId && next.id) {
        agentId = next.id;
      }

      if (!agentId && next.label) {
        const found = agents.find((a) => {
          const name = a.name ?? a.nome ?? "";
          return name === next.label;
        });
        if (found) {
          agentId =
            found.id ??
            found.id_agente ??
            found.agentId ??
            null;
        }
      }

      payload.id_agente = agentId || null;
      payload.id_usuario = null;
      updates.agentId = payload.id_agente;
      updates.agentName = next.label;
      updates.userId = null;
      updates.assignedTo = null;
      updates.userName = null;
    } else if (next.type === "user") {
      payload.id_usuario = next.id ? Number(next.id) : null;
      payload.id_agente = null;
      updates.userId = payload.id_usuario;
      updates.assignedTo = next.label;
      updates.userName = next.label;
      updates.agentId = null;
      updates.agentName = null;
    }

    updateMutation.mutate(payload, {
      onSuccess: () => {
        onUpdateConversation?.(updates);
        if (typeof onRefetchConversationDetails === "function") {
          onRefetchConversationDetails();
        }
      },
      onError: (err) => {
        console.error(t("pane.errAssignee"), err);
      },
    });
  };

  // console.log("messageHistory selectedConversation", selectedConversation);

  return (
    <Box
      className="tour-chat-message-pane"
      width={getWidth()}
      bgcolor={theme.palette.background.paper}
      display="flex"
      flexDirection="column"
      height="100%"
      sx={containerStyle}
    >
      <Box className="tour-chat-message-header" display="flex" justifyContent="space-between" alignItems="center" p={1}>
        {isMobile ? (
          <IconButton onClick={onBack}>
            <ArrowBack />
          </IconButton>
        ) : (
          !showConversationList && (
            <IconButton onClick={onBack}>
              <MenuIcon />
            </IconButton>
          )
        )}

        {selectedConversation && (
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar
              src={
                getClientProfilePictureUrl(
                  selectedConversation?.profile_picture ??
                    selectedConversation?.client?.profile_picture ??
                    selectedConversation?.client?.imagem
                ) || undefined
              }
              sx={{ width: 45, height: 45 }}
            >
              {getInitials(displayName)}
            </Avatar>

            <Box display="flex" flexDirection="column">
              <Box display="flex" alignItems="center" gap={0.75}>
                {isEditingName ? (
                  <TextField
                    size="small"
                    value={nameDraft}
                    autoFocus
                    onChange={(e) => setNameDraft(e.target.value)}
                    onBlur={() => commitName(nameDraft)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitName(nameDraft);
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        cancelEditName();
                      }
                    }}
                    inputProps={{
                      "aria-label": t("pane.editClientName"),
                      style: { fontWeight: 700 },
                    }}
                    sx={{
                      "& .MuiInputBase-root": { fontSize: "1.2rem", fontWeight: "bold" },
                      minWidth: 220,
                    }}
                  />
                ) : (
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", lineHeight: 1.2, cursor: canEditName ? "pointer" : "default" }}
                    onClick={() => {
                      if (!canEditName) return;
                      setNameDraft(selectedConversation?.nome || selectedConversation?.name || "");
                      setIsEditingName(true);
                    }}
                  >
                    {displayName}
                  </Typography>
                )}

                {canEditName && (
                  isEditingName ? (
                    <>
                      <IconButton
                        size="small"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => commitName(nameDraft)}
                        disabled={updateClientMutation.isLoading}
                        aria-label={t("pane.saveClientName")}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={cancelEditName}
                        disabled={updateClientMutation.isLoading}
                        aria-label={t("pane.cancelClientNameEdit")}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <Tooltip title={t("pane.editClientName")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setNameDraft(selectedConversation?.nome || selectedConversation?.name || "");
                            setIsEditingName(true);
                          }}
                          disabled={updateClientMutation.isLoading}
                          aria-label={t("pane.editClientName")}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )
                )}
              </Box>

              <Box mt="auto" display="flex" gap={1} alignItems="center">
                {!isMobile &&
                  displayChannel &&
                  getChannelIcon(
                    displayChannel,
                    selectedConversation?.channelType || null
                  )}

                {showWhatsAppWindowIcon && (
                  <Tooltip title={whatsAppWindowState.tooltip} placement="bottom" arrow>
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                      <ScheduleIcon
                        sx={{
                          fontSize: 22,
                          color:
                            whatsAppWindowState.status === "aboutToExpire"
                              ? theme.palette.warning.main
                              : theme.palette.error.main,
                        }}
                        aria-label={t("pane.whatsapp24h")}
                      />
                    </Box>
                  </Tooltip>
                )}

                <Tooltip title={`${t("pane.protocol")}: ${displayProtocol}`}>
                  <Chip
                    label={`${t("pane.protocol")}: ${displayProtocol}`}
                    size="small"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: "bold",
                      fontSize: "0.75rem",
                      height: "24px",
                      maxWidth: "120px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  />
                </Tooltip>

                {/* Seletor unificado: Fila / Usuário / Agente de IA */}
                {!isMobile && (
                  <AssigneeSelector
                    value={currentAssignee}
                    onChange={handleAssigneeChange}
                    users={users}
                    agents={agents}
                    size="small"
                  />
                )}

                {/* Status como chip clicável com menu de opções */}
                {!isMobile &&
                  selectedConversation?.status !== null &&
                  selectedConversation?.status !== undefined && (
                    <StatusChipSelector
                      currentStatus={selectedConversation.status}
                      onStatusChange={handleStatusChange}
                    />
                  )}
              </Box>

              {/* Tags do cliente e da solicitação */}
              {hasTags && (
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={0.5}
                  alignItems="center"
                  mt={0.5}
                >
                  {clientTags.map((tag, i) => (
                    <Chip
                      key={`c-${i}`}
                      label={tag}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "0.7rem",
                        height: 20,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  ))}
                  {requestTags.map((tag, i) => (
                    <Chip
                      key={`r-${i}`}
                      label={tag}
                      size="small"
                      color="primary"
                      sx={{
                        fontSize: "0.7rem",
                        height: 20,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Box>
          {selectedConversation?.id && (
            <IconButton onClick={onOpenPopup} title={t("pane.openPopup")}>
              <OpenInNewIcon />
            </IconButton>
          )}
          {selectedConversation && !showConversationDetails && (
            <IconButton onClick={onShowDetails}>
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Divider />

      {isLoadingDetails && !initialLoading && (
        <LinearProgress sx={{ width: "100%", position: "absolute", top: 0 }} />
      )}

      {selectedConversation && (!isLoadingDetails || !initialLoading) ? (
        <MessageHistory
          messages={messages}
          selectedConversation={selectedConversation}
          isLoadingConversation={isLoadingConversation}
          onSendMessage={onSendMessage}
          onEndConversation={onEndConversation}
          onReopenRequest={() => handleStatusChange(1)}
          onOpenNewRequest={onNewConversation}
          hasMoreMessages={hasMoreMessages}
          isLoadingMoreMessages={isLoadingMoreMessages}
          onLoadMoreMessages={onLoadMoreMessages}
          onDeleteMessage={onDeleteMessage}
        />
      ) : (
        !isMobile && <Placeholder />
      )}
    </Box>
  );
};

export default MessageHistoryPane;
